import { createMemo, For, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import {
  type ShownKind,
  type ShownTier,
  TIER_DESC_KEY,
  TIER_EXAMPLE_KEY,
  TIER_LABEL_KEY,
} from '../../lib/notification-labels';
import type {
  NotificationCellWrite,
  NotificationSettingsRow,
} from '../../lib/notification-settings';
import { flipCells, mediumState } from '../../lib/notification-tiers';
import { Panel } from '../Panel';
import { NotificationMediumControl } from './NotificationMediumControl';
import { NotificationTopicRow } from './NotificationTopicRow';

// One tier: a plain description, one example, an Email and a Browser control
// for the whole tier, and a Fine-tune disclosure (native `<details>`, as
// `KeyOutageNotice.tsx`) listing its topics with their own controls.
//
// The topic list iterates KIND IDS, not row objects: a re-read hands back new
// row objects, and `<For>` over them would rebuild every switch and drop the
// keyboard focus from the one just flipped. Strings are stable; the row is
// looked up reactively.

export function NotificationTierCard(props: {
  readonly tier: ShownTier;
  readonly rows: readonly NotificationSettingsRow[];
  readonly onWrite: (cells: readonly NotificationCellWrite[]) => void;
}): JSX.Element {
  const title = (): string => t(TIER_LABEL_KEY[props.tier]);
  const kinds = createMemo(() => props.rows.map((row) => row.kind));
  const rowOf = (kind: ShownKind): NotificationSettingsRow | undefined =>
    props.rows.find((row) => row.kind === kind);
  const flip =
    (medium: 'email' | 'push') =>
    (next: boolean): void =>
      props.onWrite(flipCells(props.rows, medium, next));

  return (
    <Panel title={title()} class="notification-tier">
      <p class="card__desc">{t(TIER_DESC_KEY[props.tier])}</p>
      <p class="notification-tier__example">
        {t('notifications_settings.tier.example_lead')} {t(TIER_EXAMPLE_KEY[props.tier])}
      </p>
      <div class="notification-tier__media">
        <NotificationMediumControl
          medium="email"
          state={mediumState(props.rows, 'email')}
          name={title()}
          onChange={flip('email')}
        />
        <NotificationMediumControl
          medium="push"
          state={mediumState(props.rows, 'push')}
          name={title()}
          onChange={flip('push')}
        />
      </div>
      <details class="notification-tier__details">
        <summary class="notification-tier__summary">
          {t('notifications_settings.tier.fine_tune')}
        </summary>
        <ul class="notification-tier__topics">
          <For each={kinds()}>
            {(kind) => (
              <Show when={rowOf(kind)}>
                {(row) => <NotificationTopicRow row={row()} onWrite={props.onWrite} />}
              </Show>
            )}
          </For>
        </ul>
      </details>
    </Panel>
  );
}
